// @flow

import {
  getPolicyDetailURLByHashid,
  POLICIES_API,
  POLICIES_VERSIONS_API,
} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {
  createErrorHandler,
  del,
  get,
  showForbiddenMessageHandler,
} from 'app/utils/request';
import {showNotification} from 'app/toasts/slice';
import {DEFAULT_PAGE_SIZE, SORT_BY_TYPES} from 'app/utils/constants';
import type {
  PoliciesDashboardFetchParamsType,
  PolicyType,
  PolicyVersionType,
} from './types';
import type {ListEndpointType} from 'app/general/types';
import {limitRequests} from 'app/utils/redux';
import type {ResponseType} from 'app/utils/redux_v2';

export const RECEIVE_POLICY_DETAIL = 'RECEIVE_POLICY_DETAIL';
export const RECEIVE_POLICY_LIST = 'RECEIVE_POLICY_LIST';
export const RECEIVE_POLICY_VERSION_DETAIL = 'RECEIVE_POLICY_VERSION_DETAIL';
export const RECEIVE_POLICIES_DASHBOARD_LIST =
  'RECEIVE_POLICIES_DASHBOARD_LIST';

/* == receive policy details ==*/

type ReceivePolicyDetailActionType = {
  type: RECEIVE_POLICY_DETAIL,
  policy: PolicyType,
};
const receivePolicyDetail = (
  policy: PolicyType
): ReceivePolicyDetailActionType => ({
  type: RECEIVE_POLICY_DETAIL,
  policy,
});

/* == receive policy version details ==*/

type ReceivePolicyVersionDetailActionType = {
  type: RECEIVE_POLICY_VERSION_DETAIL,
  version: PolicyVersionType,
};

/* == receive policy list ==*/

type ReceivePolicyListActionType = {
  type: RECEIVE_POLICY_LIST,
  policies: ListEndpointType<PolicyType>,
};
/* == PolicyActionType should contain all action types ==*/

export type PolicyActionType =
  | ReceivePolicyDetailActionType
  | ReceivePolicyListActionType
  | ReceivePolicyVersionDetailActionType;

export const receivePoliciesDashboardList = (
  query: PoliciesDashboardFetchParamsType,
  response: ResponseType<Process>
) => ({
  type: RECEIVE_POLICIES_DASHBOARD_LIST,
  query,
  response,
});

export const fetchPolicies =
  (fetchParams: PoliciesDashboardFetchParamsType) =>
  (dispatch: SimpleDispatch) =>
    requestPolicies(fetchParams).then(
      (response) =>
        dispatch(receivePoliciesDashboardList(fetchParams, response)),
      createErrorHandler(gt('Unable to load the policies'), dispatch)
    );
/* === fetch policy list === */

const requestPolicies = (params: PoliciesDashboardFetchParamsType) =>
  get(POLICIES_API, params);

export const searchPolicies = (
  search?: string,
  page_size: number = DEFAULT_PAGE_SIZE,
  search_fields: 'name' = 'name',
  ordering: string = SORT_BY_TYPES.RANK,
  page: string = '1'
) => requestPolicies({search, page, page_size, search_fields, ordering});

export const getPolicyByHashid = limitRequests(
  (hashid: string) => (dispatch: SimpleDispatch) =>
    get(getPolicyDetailURLByHashid(hashid)).then(
      (policy) => dispatch(receivePolicyDetail(policy)),
      createErrorHandler(gt('Could not fetch policies.'), dispatch, {
        '404': (error, dispatch) => {
          dispatch(showNotification('Policy not found', 'error'));
          throw error;
        },
      })
    )
);

export const fetchPolicyLatestVersion =
  (policyId: number) => (dispatch: SimpleDispatch) => {
    const errorMessage = gt('Could not fetch the latest version');

    return get(POLICIES_VERSIONS_API, {
      version_object_id: policyId,
      page_size: 1,
      page: '1',
    }).then((response) => {
      if (response.results.length === 0) {
        throw Error(`No latest found for version id ${policyId}`);
      }
      return response.results[0];
    }, createErrorHandler(errorMessage, dispatch));
  };

export const deletePolicy = (url: string) => (dispatch: FuncDispatch) =>
  del(url).then(
    () => dispatch(showNotification(gt('Policy moved to Recycle Bin'))),
    createErrorHandler(
      gt('Unable to delete policy'),
      dispatch,
      showForbiddenMessageHandler(true)
    )
  );
